/**
 * PortalSettingsCard — Settings → Client Portal tab content.
 *
 * Let firms:
 *   - Customize their public slug (drives /c/<slug>)
 *   - Pick a brand color
 *   - Toggle public signup on/off
 *   - Set portal tagline + welcome message
 *   - Configure custom intake questions
 *   - Set contact email/phone shown publicly
 *
 * Mounted at `/settings?tab=portal` for owners/admins.
 */
import { useEffect, useState } from 'react';
import { ExternalLink, Plus, Trash2, Copy, Check, Globe, Sparkles, FileSignature, Eye, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_PRESETS = [
  '#10b981', // emerald (default)
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#ef4444', // red
  '#0f172a', // slate
];

export default function PortalSettingsCard({ currentWorkspace }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    slug: '',
    brand_color: '#10b981',
    portal_tagline: '',
    portal_welcome_message: '',
    portal_public_signup_enabled: false,
    portal_intake_questions: [],
    portal_contact_email: '',
    portal_contact_phone: '',
    portal_engagement_letter_enabled: true,
    portal_engagement_letter_template: '',
    portal_engagement_letter_fee_summary: '',
  });
  // Server-provided defaults + merge-field legend; populated by the GET.
  const [letterDefaults, setLetterDefaults] = useState({
    template: '',
    fee_summary: '',
    merge_fields: [],
  });
  const [previewing, setPreviewing] = useState(false);
  const [copied, setCopied] = useState(false);
  const wsId = currentWorkspace?.workspace_id;

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/portal-settings`);
        if (r.ok) {
          const j = await r.json();
          setSettings({
            slug: j.slug || '',
            brand_color: j.brand_color || '#10b981',
            portal_tagline: j.portal_tagline || '',
            portal_welcome_message: j.portal_welcome_message || '',
            portal_public_signup_enabled: !!j.portal_public_signup_enabled,
            portal_intake_questions: j.portal_intake_questions || [],
            portal_contact_email: j.portal_contact_email || '',
            portal_contact_phone: j.portal_contact_phone || '',
            portal_engagement_letter_enabled: j.portal_engagement_letter_enabled !== false,
            portal_engagement_letter_template: j.portal_engagement_letter_template || '',
            portal_engagement_letter_fee_summary: j.portal_engagement_letter_fee_summary || '',
          });
          setLetterDefaults({
            template: j.engagement_letter_default_template || '',
            fee_summary: j.engagement_letter_default_fee_summary || '',
            merge_fields: j.engagement_letter_merge_fields || [],
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [wsId]);

  const portalUrl = settings.slug ? `${window.location.origin}/c/${settings.slug}` : '';
  const signupUrl = settings.slug ? `${window.location.origin}/c/${settings.slug}/start` : '';

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/portal-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Save failed');
        return;
      }
      const updated = await r.json();
      setSettings({ ...settings, slug: updated.slug, brand_color: updated.brand_color });
      toast.success('Portal settings saved');
    } catch {
      toast.error('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success('Link copied to clipboard');
  };

  const addIntakeQuestion = () => {
    const id = `q_${Date.now().toString(36)}`;
    setSettings({
      ...settings,
      portal_intake_questions: [
        ...settings.portal_intake_questions,
        { id, label: 'New question', type: 'text', required: false, options: [], help_text: '' },
      ],
    });
  };

  const updateIntakeQuestion = (idx, patch) => {
    const next = [...settings.portal_intake_questions];
    next[idx] = { ...next[idx], ...patch };
    setSettings({ ...settings, portal_intake_questions: next });
  };

  const removeIntakeQuestion = (idx) => {
    setSettings({
      ...settings,
      portal_intake_questions: settings.portal_intake_questions.filter((_, i) => i !== idx),
    });
  };

  const restoreEngagementLetterDefault = () => {
    setSettings({
      ...settings,
      portal_engagement_letter_template: letterDefaults.template,
      portal_engagement_letter_fee_summary: letterDefaults.fee_summary,
    });
    toast.success('Restored default engagement letter');
  };

  const previewEngagementLetter = async () => {
    setPreviewing(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${wsId}/portal-settings/engagement-letter/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            template: settings.portal_engagement_letter_template,
            fee_summary: settings.portal_engagement_letter_fee_summary,
          }),
        }
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Preview failed');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke after a generous delay so the new tab has time to fetch the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Network error generating preview');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded-2xl" />;
  }

  return (
    <div className="space-y-6" data-testid="portal-settings-card">
      {/* Public URLs panel */}
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-5 h-5" style={{ color: settings.brand_color }} />
          <h3 className="font-semibold text-lg">Your public portal</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Share these links with clients. Anyone can sign up at the signup URL when public signup is enabled.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Landing page</Label>
            <div className="mt-1 flex gap-2">
              <Input value={portalUrl} readOnly className="font-mono text-sm" data-testid="portal-landing-url" />
              <Button
                variant="outline" size="sm" onClick={() => copyUrl(portalUrl)}
                data-testid="copy-portal-url"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={portalUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Signup link</Label>
            <div className="mt-1 flex gap-2">
              <Input value={signupUrl} readOnly className="font-mono text-sm" data-testid="portal-signup-url" />
              <Button variant="outline" size="sm" onClick={() => copyUrl(signupUrl)}>
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={signupUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Branding panel */}
      <div className="rounded-2xl border bg-card p-6">
        <h3 className="font-semibold text-lg mb-1">Branding</h3>
        <p className="text-sm text-muted-foreground mb-5">
          How your portal looks to clients. Your logo carries over from Settings → Workspace.
        </p>
        <div className="space-y-5">
          <div>
            <Label htmlFor="portal-slug">URL slug</Label>
            <p className="text-xs text-muted-foreground mb-2">Letters, numbers, hyphens only. Min 3 characters.</p>
            <Input
              id="portal-slug"
              value={settings.slug}
              onChange={(e) => setSettings({ ...settings, slug: e.target.value })}
              placeholder="my-firm"
              className="font-mono"
              data-testid="portal-slug-input"
            />
          </div>

          <div>
            <Label>Brand color</Label>
            <p className="text-xs text-muted-foreground mb-2">Used for buttons, links, and accents on your portal.</p>
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSettings({ ...settings, brand_color: c })}
                    className="w-8 h-8 rounded-lg ring-2 ring-offset-2 ring-offset-background transition-all"
                    style={{
                      background: c,
                      ringColor: settings.brand_color === c ? c : 'transparent',
                    }}
                    aria-label={`Pick color ${c}`}
                    data-testid={`color-preset-${c.slice(1)}`}
                  />
                ))}
              </div>
              <Input
                value={settings.brand_color}
                onChange={(e) => setSettings({ ...settings, brand_color: e.target.value })}
                className="font-mono w-32"
                placeholder="#10b981"
                data-testid="portal-color-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="portal-tagline">Headline</Label>
            <p className="text-xs text-muted-foreground mb-2">The big greeting on your landing page (max 140 chars).</p>
            <Input
              id="portal-tagline"
              value={settings.portal_tagline}
              onChange={(e) => setSettings({ ...settings, portal_tagline: e.target.value })}
              maxLength={140}
              placeholder={`Welcome to ${currentWorkspace?.name || 'your firm'}.`}
              data-testid="portal-tagline-input"
            />
          </div>

          <div>
            <Label htmlFor="portal-welcome">Welcome message</Label>
            <p className="text-xs text-muted-foreground mb-2">Subheading paragraph shown below the headline (max 500 chars).</p>
            <Textarea
              id="portal-welcome"
              value={settings.portal_welcome_message}
              onChange={(e) => setSettings({ ...settings, portal_welcome_message: e.target.value })}
              maxLength={500}
              rows={3}
              placeholder="Tell new clients what to expect when they sign up…"
              data-testid="portal-welcome-input"
            />
          </div>
        </div>
      </div>

      {/* Public signup panel */}
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-1.5">
              Public signup
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-500/30 inline-flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> New
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Let prospective clients sign up themselves at <code className="text-xs">/c/{settings.slug}/start</code>.
            </p>
          </div>
          <Switch
            checked={settings.portal_public_signup_enabled}
            onCheckedChange={(c) => setSettings({ ...settings, portal_public_signup_enabled: c })}
            data-testid="portal-public-signup-toggle"
          />
        </div>

        {settings.portal_public_signup_enabled && (
          <div className="mt-5 space-y-5 pt-5 border-t">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Custom intake questions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addIntakeQuestion} data-testid="add-intake-question">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add question
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Ask new clients anything you need to know before their first call (e.g., business type, services
                interested in, current bookkeeping software).
              </p>
              <div className="space-y-3">
                {settings.portal_intake_questions.length === 0 && (
                  <p className="text-sm text-muted-foreground italic py-4 text-center bg-muted/40 rounded-lg">
                    No custom questions yet. The standard fields (name, email, business, phone, message) are always shown.
                  </p>
                )}
                {settings.portal_intake_questions.map((q, idx) => (
                  <div key={q.id} className="rounded-lg border p-3 space-y-2" data-testid={`intake-q-${q.id}`}>
                    <div className="flex gap-2">
                      <Input
                        value={q.label}
                        onChange={(e) => updateIntakeQuestion(idx, { label: e.target.value })}
                        placeholder="Question label"
                        className="flex-1"
                      />
                      <Select value={q.type} onValueChange={(v) => updateIntakeQuestion(idx, { type: v })}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Short text</SelectItem>
                          <SelectItem value="textarea">Long text</SelectItem>
                          <SelectItem value="select">Dropdown</SelectItem>
                          <SelectItem value="checkbox">Yes/No</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button" variant="ghost" size="sm"
                        onClick={() => removeIntakeQuestion(idx)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-intake-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {q.type === 'select' && (
                      <Input
                        value={(q.options || []).join(', ')}
                        onChange={(e) => updateIntakeQuestion(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="Option 1, Option 2, Option 3"
                        className="text-sm"
                      />
                    )}
                    <div className="flex items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={q.required}
                          onChange={(e) => updateIntakeQuestion(idx, { required: e.target.checked })}
                          className="rounded"
                        />
                        Required
                      </label>
                      <Input
                        value={q.help_text || ''}
                        onChange={(e) => updateIntakeQuestion(idx, { help_text: e.target.value })}
                        placeholder="Help text (optional)"
                        className="flex-1 text-xs h-8"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Engagement letter panel */}
      <div className="rounded-2xl border bg-card p-6" data-testid="engagement-letter-card">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <FileSignature className="w-5 h-5" style={{ color: settings.brand_color }} />
              Engagement letter
              <span className="px-1.5 py-0.5 rounded-md bg-violet-500/15 text-[10px] font-semibold text-violet-700 dark:text-violet-400 ring-1 ring-violet-500/30 inline-flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> New
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Auto-send a plain-English engagement letter for e-signature right after a new client signs up.
            </p>
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-emerald-600 hover:text-emerald-500 select-none font-medium" data-testid="engagement-letter-help">
                What is an engagement letter? →
              </summary>
              <div className="mt-2 p-3 rounded-lg bg-muted/40 border text-muted-foreground leading-relaxed space-y-1.5">
                <p>
                  An engagement letter is a short written agreement between you and a new client that
                  spells out:
                </p>
                <ul className="list-disc list-inside space-y-0.5 pl-1">
                  <li>What services you'll provide</li>
                  <li>How and when they'll pay you</li>
                  <li>Each side's responsibilities and confidentiality terms</li>
                </ul>
                <p>
                  Most CPAs, bookkeepers, and contractors send one before starting work to avoid
                  scope creep and billing disputes. CountBucks ships with a sensible default you can
                  customize — leave the toggle OFF if you don't want clients to receive one.
                </p>
              </div>
            </details>
          </div>
          <Switch
            checked={settings.portal_engagement_letter_enabled}
            onCheckedChange={(c) =>
              setSettings({ ...settings, portal_engagement_letter_enabled: c })
            }
            data-testid="portal-engagement-letter-toggle"
          />
        </div>

        {settings.portal_engagement_letter_enabled && (
          <div className="mt-5 space-y-5 pt-5 border-t">
            {/* Merge-field legend */}
            {letterDefaults.merge_fields.length > 0 && (
              <div className="rounded-lg bg-muted/40 p-3 text-xs">
                <div className="font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
                  Available merge fields (use <code className="px-1 rounded bg-zinc-200/70 dark:bg-zinc-800">{`{{key}}`}</code>):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {letterDefaults.merge_fields.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-[11px] font-mono"
                      title={f.desc}
                    >
                      {`{{${f.key}}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="engagement-letter-template">Letter body</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Plain text with <code>**double-asterisks**</code> for bold headings. The letter header
                (firm name + addressee) and the signature block at the bottom are added automatically.
              </p>
              <Textarea
                id="engagement-letter-template"
                value={settings.portal_engagement_letter_template}
                onChange={(e) =>
                  setSettings({ ...settings, portal_engagement_letter_template: e.target.value })
                }
                placeholder={letterDefaults.template}
                rows={14}
                className="font-mono text-xs leading-relaxed"
                maxLength={20000}
                data-testid="engagement-letter-template-textarea"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Leave blank to use the built-in default template.
              </p>
            </div>

            <div>
              <Label htmlFor="engagement-letter-fee">Fee summary</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Shown in the &quot;Fees&quot; section (via the <code>{`{{fee_summary}}`}</code> merge field).
              </p>
              <Textarea
                id="engagement-letter-fee"
                value={settings.portal_engagement_letter_fee_summary}
                onChange={(e) =>
                  setSettings({ ...settings, portal_engagement_letter_fee_summary: e.target.value })
                }
                placeholder={letterDefaults.fee_summary}
                rows={3}
                maxLength={2000}
                data-testid="engagement-letter-fee-textarea"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={previewEngagementLetter}
                disabled={previewing}
                data-testid="engagement-letter-preview-btn"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                {previewing ? 'Generating…' : 'Preview PDF'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={restoreEngagementLetterDefault}
                data-testid="engagement-letter-restore-default-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Restore default
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Contact info panel */}
      <div className="rounded-2xl border bg-card p-6">
        <h3 className="font-semibold text-lg mb-1">Public contact info</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Shown on your portal landing page so prospects can reach you directly.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="portal-contact-email">Email</Label>
            <Input
              id="portal-contact-email"
              type="email"
              value={settings.portal_contact_email}
              onChange={(e) => setSettings({ ...settings, portal_contact_email: e.target.value })}
              placeholder="hello@yourfirm.com"
              data-testid="portal-contact-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="portal-contact-phone">Phone</Label>
            <Input
              id="portal-contact-phone"
              value={settings.portal_contact_phone}
              onChange={(e) => setSettings({ ...settings, portal_contact_phone: e.target.value })}
              placeholder="(555) 555-0100"
              data-testid="portal-contact-phone"
            />
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="flex justify-end gap-2 sticky bottom-4 z-10">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="text-white shadow-lg"
          style={{ background: settings.brand_color }}
          data-testid="portal-settings-save"
        >
          {saving ? 'Saving…' : 'Save portal settings'}
        </Button>
      </div>
    </div>
  );
}
